import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import {
  apiErrorMessage,
  fetchInbox,
  fetchInboxStatus,
  runInbox,
  uploadToInbox,
  type InboxLogRow,
  type InboxStatus,
} from "../services/expensesService";
import { Chip, ErrorBox, FileDrop, Notice, Page, Spinner } from "../components/ui";
import { INBOX_TONES, ui } from "../theme";
import { dateTime } from "../lib/format";

// Buzón de facturación (#611): estado de la conexión IMAP, revisión manual,
// carga manual de facturas y la bitácora de cada correo procesado.

export default function InboxSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [status, setStatus] = useState<InboxStatus | null>(null);
  const [log, setLog] = useState<InboxLogRow[]>([]);
  const [busy, setBusy] = useState<"" | "run" | "upload">("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([fetchInboxStatus(), fetchInbox()]);
      setStatus(s);
      setLog(l);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || "load");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async () => {
    setBusy("run");
    setNotice(null);
    try {
      const r = await runInbox();
      setNotice(r.configured ? t("expenses.inbox.runDone", { processed: r.processed, failed: r.failed }) : t("expenses.inbox.notConfigured"));
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || "run");
    } finally {
      setBusy("");
    }
  };

  const upload = async (files: File[]) => {
    setBusy("upload");
    setNotice(null);
    try {
      const row = await uploadToInbox(files);
      setNotice(t(`expenses.inbox.result.${row.result}`) + (row.detail ? ` — ${row.detail}` : ""));
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || "upload");
    } finally {
      setBusy("");
    }
  };

  const errorText = error && ["load", "run", "upload"].includes(error) ? t(`expenses.errors.${error === "run" ? "inbox" : error}`) : error;
  const lastRun = status?.state?.lastRunAt;

  return (
    <Page
      title={t("expenses.menu.inbox")}
      subtitle={t("expenses.inbox.subtitle")}
      actions={
        <button type="button" className={c.primary} disabled={!status?.configured || !status?.linkedToThisCompany || Boolean(busy)} onClick={run}>
          <i className="ri-mail-download-line mr-1" />
          {t("expenses.inbox.runNow")}
        </button>
      }
    >
      <ErrorBox message={errorText} />
      {notice && <Notice tone="ok">{notice}</Notice>}

      <div className={`${c.card} grid grid-cols-1 gap-4 sm:grid-cols-3`}>
        <div>
          <p className={`text-xs ${c.muted}`}>{t("expenses.inbox.address")}</p>
          <p className={`mt-1 font-semibold ${c.text}`}>{status?.address || "—"}</p>
          <p className="mt-1">
            {status == null ? (
              <Spinner />
            ) : status.configured ? (
              status.linkedToThisCompany ? (
                <Chip className="bg-emerald-500/15 text-emerald-600">{t("expenses.inbox.connected")}</Chip>
              ) : (
                <Chip className="bg-amber-500/15 text-amber-600">{t("expenses.inbox.otherCompany")}</Chip>
              )
            ) : (
              <Chip className="bg-gray-400/15 text-gray-500">{t("expenses.inbox.notConfiguredShort")}</Chip>
            )}
          </p>
        </div>
        <div>
          <p className={`text-xs ${c.muted}`}>{t("expenses.inbox.lastRun")}</p>
          <p className={`mt-1 ${c.text}`}>{dateTime(lastRun, i18n.language)}</p>
          {status?.state?.lastError && <p className="mt-1 text-xs text-red-500">{status.state.lastError}</p>}
        </div>
        <div>
          <p className={`text-xs ${c.muted}`}>{t("expenses.inbox.howItWorks")}</p>
          <p className={`mt-1 text-xs ${c.text}`}>{t("expenses.inbox.howItWorksText")}</p>
        </div>
      </div>
      {status && !status.configured && <Notice tone="warn">{t("expenses.inbox.setupHelp")}</Notice>}
      {status && !status.aiConfigured && <Notice tone="warn">{t("expenses.inbox.pdfNeedsAi")}</Notice>}

      <div className={c.card}>
        <FileDrop
          accept="application/pdf,.xml,text/xml,application/xml"
          multiple
          disabled={Boolean(busy)}
          label={t("expenses.inbox.manualUpload")}
          hint={t("expenses.inbox.manualUploadHint")}
          onFiles={upload}
        />
        {busy === "upload" && (
          <div className="mt-2">
            <Spinner label={t("expenses.inbox.processing")} />
          </div>
        )}
      </div>

      <div className={`${c.card} overflow-x-auto p-0`}>
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className={`text-left text-xs ${c.muted}`}>
              <th className="px-4 py-2">{t("expenses.inbox.date")}</th>
              <th className="px-2 py-2">{t("expenses.inbox.from")}</th>
              <th className="px-2 py-2">{t("expenses.inbox.files")}</th>
              <th className="px-2 py-2">{t("expenses.inbox.resultCol")}</th>
              <th className="px-4 py-2">{t("expenses.inbox.detail")}</th>
            </tr>
          </thead>
          <tbody>
            {log.map((r) => (
              <tr key={r.id} className={`border-t ${c.border} ${c.text}`}>
                <td className="whitespace-nowrap px-4 py-2 text-xs tabular-nums">{dateTime(r.processedAt, i18n.language)}</td>
                <td className="px-2 py-2">
                  <p className="max-w-xs truncate">{r.subject || "—"}</p>
                  <p className={`max-w-xs truncate text-xs ${c.muted}`}>{r.from}</p>
                </td>
                <td className={`px-2 py-2 text-xs ${c.muted}`}>{r.attachments.map((a) => a.name).join(", ") || "—"}</td>
                <td className="px-2 py-2">
                  {(r.results || [{ result: r.result }]).map((x, i) => (
                    <Chip key={i} className={`mr-1 ${INBOX_TONES[x.result]}`}>
                      {t(`expenses.inbox.result.${x.result}`)}
                    </Chip>
                  ))}
                  {r.retryable && <Chip className="bg-amber-500/15 text-amber-600">{t("expenses.inbox.willRetry")}</Chip>}
                </td>
                <td className={`px-4 py-2 text-xs ${c.muted}`}>{r.detail || "—"}</td>
              </tr>
            ))}
            {log.length === 0 && (
              <tr>
                <td colSpan={5} className={`px-4 py-8 text-center ${c.muted}`}>
                  {t("expenses.inbox.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
